import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  CircularProgress,
  Chip,
  Grid,
  InputAdornment,
  Collapse,
  Divider
} from '@mui/material';
import { toast } from 'react-toastify';
import {
  RefreshCw,
  Eye,
  CheckCircle2,
  XCircle,
  Pencil,
  Wand2,
  Upload,
  HelpCircle,
  User,
  PhoneCall,
  Mail,
  Search,
  Filter,
  LayoutGrid,
  AlertCircle,
  FileText,
  Sparkles,
  ClipboardList,
  UserCheck
} from 'lucide-react';
import api from '../../services/api';
import LoaderProgress from '../../components/ui/loader-progress';

function formatDateTime(value) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('it-IT');
}

function InfoRow({ icon: Icon, title, description, color, bgColor }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        p: 1.75,
        borderRadius: 2,
        border: '1px solid rgba(0,0,0,0.12)',
        bgcolor: '#fff'
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 2,
          bgcolor: bgColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: '0 0 auto'
        }}
      >
        <Icon size={18} color={color} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 800, fontSize: 15, color: 'text.primary' }}>
          {title}
        </Typography>
        <Typography
          variant="body2"
          sx={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.45, color: 'rgba(0,0,0,0.72)' }}
        >
          {description}
        </Typography>
      </Box>
    </Box>
  );
}

function confidenceChipProps(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) return { label: '-', color: 'default' };
  if (n >= 90) return { label: `${n}%`, color: 'success' };
  if (n >= 75) return { label: `${n}%`, color: 'warning' };
  return { label: `${n}%`, color: 'error' };
}

function matchingChipProps(value) {
  const v = String(value || '');
  if (v === 'confermato') return { label: 'Confermato', color: 'success' };
  if (v === 'corretto') return { label: 'Corretto', color: 'info' };
  if (v === 'rifiutato') return { label: 'Rifiutato', color: 'default' };
  if (v === 'in_attesa') return { label: 'In attesa', color: 'warning' };
  return { label: v || '-', color: 'default' };
}

const MATCH_STATUS_OPTIONS = ['in_attesa', 'confermato', 'corretto', 'rifiutato'];

function renderCustomerPreview(customer, fallback) {
  const name = customer ? [customer.name, customer.surname].filter(Boolean).join(' ').trim() : '';
  const phone = customer && customer.telephone ? String(customer.telephone) : '';
  const email = customer && customer.email ? String(customer.email) : '';

  const title = name || email || phone || fallback || '-';

  return (
    <Box sx={{ display: 'grid', gap: 0.6, minWidth: 240 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <User size={18} color="#2563eb" />
        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
          {title}
        </Typography>
      </Box>
      {phone ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PhoneCall size={18} color="#64748b" />
          <Typography variant="body2" color="text.secondary">
            {phone}
          </Typography>
        </Box>
      ) : null}
      {email ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Mail size={18} color="#64748b" />
          <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
            {email}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
}

export default function AderCodaValidazione() {
  const navigate = useNavigate();
  const [helpOpen, setHelpOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [refreshKey, setRefreshKey] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [denseTable, setDenseTable] = useState(true);
  const [tipoOptions, setTipoOptions] = useState([]);

  const [filterCustomerSearch, setFilterCustomerSearch] = useState('');
  const [filterCustomerOptions, setFilterCustomerOptions] = useState([]);
  const [filterCustomerLoading, setFilterCustomerLoading] = useState(false);
  const [filterCustomerSelected, setFilterCustomerSelected] = useState(null);
  const [filterTipoSelected, setFilterTipoSelected] = useState(null);
  const [filterMatchStatusesSelected, setFilterMatchStatusesSelected] = useState([]);

  const [correctDialogOpen, setCorrectDialogOpen] = useState(false);
  const [correctTargetId, setCorrectTargetId] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerSelected, setCustomerSelected] = useState(null);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/ader/coda-validazione', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          stati: 'caricato,in_elaborazione,da_validare,elaborato,associato,errore',
          ...(searchQuery ? { q: searchQuery } : {}),
          ...(filterCustomerSelected?.id ? { cliente_id: filterCustomerSelected.id } : {}),
          ...(filterTipoSelected ? { tipo_documento: filterTipoSelected } : {}),
          ...(filterMatchStatusesSelected.length
            ? { matching_stato_validazione: filterMatchStatusesSelected.join(',') }
            : {})
        }
      });
      setRows(res.data.data || []);
      setTotal(res.data.total || 0);
      setTipoOptions((prev) => {
        const set = new Set([...(prev || [])]);
        for (const r of res.data.data || []) {
          if (r?.tipo_documento) set.add(String(r.tipo_documento));
        }
        return Array.from(set).sort((a, b) => a.localeCompare(b));
      });
    } catch (error) {
      toast.error('Errore caricando la coda validazione ADER');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filterCustomerSelected, filterTipoSelected, filterMatchStatusesSelected, searchQuery]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue, refreshKey]);

  useEffect(() => {
    setPage(0);
  }, [filterCustomerSelected, filterTipoSelected, filterMatchStatusesSelected, searchQuery]);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearchQuery(String(searchInput || '').trim());
    }, 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    let active = true;
    const term = String(filterCustomerSearch || '').trim();
    if (term.length < 2) {
      setFilterCustomerOptions([]);
      return undefined;
    }
    setFilterCustomerLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await api.get('/customer', { params: { search: term, page: 1, limit: 10 } });
        if (!active) return;
        setFilterCustomerOptions(res.data.data || []);
      } catch (error) {
        if (!active) return;
        setFilterCustomerOptions([]);
      } finally {
        if (active) setFilterCustomerLoading(false);
      }
    }, 250);
    return () => {
      active = false;
      clearTimeout(t);
    };
  }, [filterCustomerSearch]);

  useEffect(() => {
    let active = true;
    const term = String(customerSearch || '').trim();
    if (!correctDialogOpen) return undefined;
    if (term.length < 2) {
      setCustomerOptions([]);
      return undefined;
    }
    setCustomerLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await api.get('/customer', {
          params: { search: term, page: 1, limit: 10 }
        });
        if (!active) return;
        setCustomerOptions(res.data.data || []);
      } catch (error) {
        if (!active) return;
        setCustomerOptions([]);
      } finally {
        if (active) setCustomerLoading(false);
      }
    }, 300);
    return () => {
      active = false;
      clearTimeout(t);
    };
  }, [customerSearch, correctDialogOpen]);

  const customerOptionLabel = useMemo(() => {
    return (option) => {
      const name = [option?.name, option?.surname].filter(Boolean).join(' ').trim();
      const cf = String(option?.codice_fiscale || '').trim();
      return [name || `ID ${option?.id}`, cf ? `CF ${cf}` : ''].filter(Boolean).join(' • ');
    };
  }, []);

  const clearFilters = () => {
    setFilterCustomerSearch('');
    setFilterCustomerOptions([]);
    setFilterCustomerSelected(null);
    setFilterTipoSelected(null);
    setFilterMatchStatusesSelected([]);
    setSearchInput('');
    setRefreshKey((v) => v + 1);
  };

  const handleValidate = async ({ documentoId, action, clienteId }) => {
    try {
      await api.post(`/ader/documenti/${documentoId}/valida-matching`, {
        action,
        cliente_id: clienteId
      });
      toast.success('Matching aggiornato');
      setRefreshKey((v) => v + 1);
    } catch (error) {
      toast.error('Errore aggiornando il matching');
    }
  };

  const handleRecalcMatching = async (documentoId) => {
    try {
      await api.post(`/ader/documenti/${documentoId}/matching`);
      toast.success('Matching ricalcolato');
      setRefreshKey((v) => v + 1);
    } catch (error) {
      toast.error('Errore ricalcolando il matching');
    }
  };

  const openCorrectDialog = (documentoId) => {
    setCorrectTargetId(Number(documentoId));
    setCustomerSearch('');
    setCustomerOptions([]);
    setCustomerSelected(null);
    setCorrectDialogOpen(true);
  };

  const confirmCorrection = async () => {
    if (!correctTargetId) return;
    if (!customerSelected?.id) {
      toast.error('Seleziona un cliente');
      return;
    }
    await handleValidate({
      documentoId: correctTargetId,
      action: 'correggi',
      clienteId: customerSelected.id
    });
    setCorrectDialogOpen(false);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Card sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                ADER · Coda validazione
              </Typography>
              <Tooltip title="Come funziona l’elaborazione">
                <IconButton size="small" onClick={() => setHelpOpen(true)}>
                  <HelpCircle size={18} />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Documenti in stato “da_validare” con classificazione/estrazione AI e matching cliente.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<Upload size={18} />}
              onClick={() => navigate('/ader/documenti/upload')}
            >
              Carica
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshCw size={18} />}
              onClick={() => setRefreshKey((v) => v + 1)}
            >
              Aggiorna
            </Button>
          </Box>
        </Box>
      </Card>

      <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Come funziona l’elaborazione ADER</DialogTitle>
        <DialogContent dividers>
          <InfoRow
            icon={AlertCircle}
            title="In breve"
            description="Il sistema estrae prima il testo dal PDF e poi usa l’AI per classificare ed estrarre i dati. In base al tipo documento può attivare estrattori specifici.\nSe mancano dati o la confidence è bassa, il documento passa in coda per verifica."
            color="#b45309"
            bgColor="#fef3c7"
          />

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'grid', gap: 1.5 }}>
            <InfoRow
              icon={FileText}
              title="1) Estrazione base (PDF nativi)"
              description="Popola `testo_estratto`. Se il testo è insufficiente o il PDF è una scansione, il documento resta da validare."
              color="#0284c7"
              bgColor="#e0f2fe"
            />
            <InfoRow
              icon={Sparkles}
              title="2) Classificazione AI"
              description="Determina il tipo documento e una confidence. Se la confidence è bassa, richiede intervento operatore."
              color="#7c3aed"
              bgColor="#ede9fe"
            />
            <InfoRow
              icon={ClipboardList}
              title="3) Estrazione per tipo"
              description="Attiva estrattori specifici quando disponibili: “estratto di ruolo” → righe tabellari; “cartella di pagamento” → voci/importi."
              color="#c2410c"
              bgColor="#ffedd5"
            />
            <InfoRow
              icon={UserCheck}
              title="4) Matching cliente + validazione"
              description="Propone un cliente; se il matching è “in attesa” o richiede controllo, rimane in questa coda."
              color="#16a34a"
              bgColor="#dcfce7"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpOpen(false)}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      <Card sx={{ mb: 2, overflow: 'hidden' }}>
        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Cerca per file, CF, nominativo, numero cartella/atto…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end" sx={{ gap: 0.5 }}>
                  <Tooltip title="Filtri">
                    <IconButton size="small" onClick={() => setFiltersOpen((v) => !v)}>
                      <Filter size={18} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={denseTable ? 'Vista compatta' : 'Vista estesa'}>
                    <IconButton size="small" onClick={() => setDenseTable((v) => !v)}>
                      <LayoutGrid size={18} />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              )
            }}
          />
          <Collapse in={filtersOpen} timeout="auto" unmountOnExit>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={filterCustomerOptions}
                  loading={filterCustomerLoading}
                  value={filterCustomerSelected}
                  onChange={(_, val) => setFilterCustomerSelected(val)}
                  inputValue={filterCustomerSearch}
                  onInputChange={(_, val) => setFilterCustomerSearch(val)}
                  getOptionLabel={customerOptionLabel}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      {renderCustomerPreview(option, `ID ${option?.id}`)}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Cliente"
                      placeholder="Nome, CF, email, telefono…"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search size={18} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <>
                            {filterCustomerLoading ? <CircularProgress color="inherit" size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  options={tipoOptions}
                  value={filterTipoSelected}
                  onChange={(_, val) => setFilterTipoSelected(val)}
                  renderInput={(params) => (
                    <TextField {...params} size="small" label="Tipo (AI)" placeholder="Seleziona" />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  multiple
                  options={MATCH_STATUS_OPTIONS}
                  value={filterMatchStatusesSelected}
                  onChange={(_, val) => setFilterMatchStatusesSelected(val || [])}
                  getOptionLabel={(option) => matchingChipProps(option).label}
                  renderOption={(props, option) => {
                    const p = matchingChipProps(option);
                    return (
                      <Box component="li" {...props}>
                        <Chip size="small" label={p.label} color={p.color} />
                      </Box>
                    );
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const p = matchingChipProps(option);
                      return <Chip {...getTagProps({ index })} key={option} size="small" label={p.label} color={p.color} />;
                    })
                  }
                  renderInput={(params) => (
                    <TextField {...params} size="small" label="Stato match" placeholder="Seleziona" />
                  )}
                />
              </Grid>
            </Grid>
          </Collapse>
        </Box>

        <TableContainer>
          <Table size={denseTable ? 'small' : 'medium'}>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Documento</TableCell>
                <TableCell>Tipo (AI)</TableCell>
                <TableCell align="right">Conf.</TableCell>
                <TableCell>Cliente suggerito</TableCell>
                <TableCell align="right">Match</TableCell>
                <TableCell>Stato match</TableCell>
                <TableCell>Caricato</TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <LoaderProgress size={50} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      Nessun documento in coda
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const confidence = confidenceChipProps(row.confidence_classificazione);
                  const matchConfidence = confidenceChipProps(row.confidence_matching);
                  const matchStatus = matchingChipProps(row.matching_stato_validazione);
                  const suggestedId = row.cliente_suggerito_id || row.cliente_id || null;

                  return (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ fontWeight: 700 }}>{row.id}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {row.nome_file || '-'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.mime_type || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>{row.tipo_documento || '-'}</TableCell>
                      <TableCell align="right">
                        <Chip size="small" label={confidence.label} color={confidence.color} />
                      </TableCell>
                      <TableCell>
                        {renderCustomerPreview(row.cliente_suggerito, suggestedId ? `ID ${suggestedId}` : '-')}
                      </TableCell>
                      <TableCell align="right">
                        <Chip size="small" label={matchConfidence.label} color={matchConfidence.color} />
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={matchStatus.label} color={matchStatus.color} />
                      </TableCell>
                      <TableCell>{formatDateTime(row.data_caricamento)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Apri dettaglio">
                          <IconButton size="small" onClick={() => navigate(`/ader/documenti/${row.id}`)}>
                            <Eye size={18} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Ricalcola matching">
                          <IconButton size="small" onClick={() => handleRecalcMatching(row.id)}>
                            <Wand2 size={18} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Conferma">
                          <span>
                            <IconButton
                              size="small"
                              disabled={!row.cliente_suggerito_id}
                              onClick={() => handleValidate({ documentoId: row.id, action: 'conferma' })}
                            >
                              <CheckCircle2 size={18} />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Rifiuta">
                          <IconButton
                            size="small"
                            onClick={() => handleValidate({ documentoId: row.id, action: 'rifiuta' })}
                          >
                            <XCircle size={18} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Correggi">
                          <IconButton size="small" onClick={() => openCorrectDialog(row.id)}>
                            <Pencil size={18} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 20, 50]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Righe per pagina:"
        />
      </Card>

      <Dialog open={correctDialogOpen} onClose={() => setCorrectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Correggi cliente</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                fullWidth
                loading={customerLoading}
                options={customerOptions}
                value={customerSelected}
                onChange={(_, newValue) => setCustomerSelected(newValue)}
                inputValue={customerSearch}
                onInputChange={(_, newInputValue) => setCustomerSearch(newInputValue)}
                getOptionLabel={customerOptionLabel}
                isOptionEqualToValue={(opt, val) => Number(opt?.id) === Number(val?.id)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Cerca cliente"
                    placeholder="Nome, cognome, CF, email..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {customerLoading ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                L’azione “Correggi” imposta il matching come “corretto” e aggiorna anche `ai_ader_documenti.cliente_id`.
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCorrectDialogOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={confirmCorrection}>
            Conferma
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
